import { skipToken } from '@reduxjs/toolkit/query';
import { setupAgGridLicence } from '@sgme/aggrid';
import { isDefined } from '@sgme/fp';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { ColumnsToolPanelModule, ExcelExportModule, SetFilterModule, TreeDataModule } from 'ag-grid-enterprise';
import { memo, useCallback, useMemo } from 'react';

import { BlotterPanel } from '@/App/Blotter/BlotterPanel.tsx';
import { BlotterTrigger } from '@/App/Blotter/BlotterTrigger.tsx';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { useUserContext } from '@/context/UserContext.tsx';
import { blotterApi } from '@/store/blotter/api/index.ts';
import { computeRangeForCustomPresetISO } from '@/store/blotter/daterangeCustomPresets.ts';
import { blotterSlice } from '@/store/blotter/slice.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';

import './ag-common.css';

setupAgGridLicence();
ModuleRegistry.registerModules([
  AllCommunityModule,
  ExcelExportModule,
  SetFilterModule,
  ColumnsToolPanelModule,
  TreeDataModule,
]);

export const Blotter = memo(
  // protecting against abusive re-renders
  () => {
    const dispatch = useAppDispatch();
    const track = useTrackEvent();

    const panelIsOpen = useAppSelector(blotterSlice.selectors.getPanelIsOpen);

    const togglePanelVisibility = useCallback(() => {
      track(panelIsOpen ? 'Close' : 'Open'); // this isn't supposed to take too long (pushes in some array) : measured <3ms
      dispatch(blotterSlice.actions.togglePanel());
    }, [panelIsOpen]);

    //
    // Blotter data feed
    //
    const isBlotterReady = useAppSelector(blotterSlice.selectors.checkReady);
    const blotterFilter = useAppSelector(blotterSlice.selectors.getFilter) ?? { type: 'intraday' };
    const { showMyMainDeals } = useAppSelector(getUserPreferences);
    const {
      currentUser: { clients },
    } = useUserContext();

    const counterPartyBdrInitials = useMemo(() => clients.map((client) => client.companyId.toString()), [clients]);

    const [fromDate, toDate] = useMemo(
      () =>
        blotterFilter.type === 'intraday'
          ? []
          : blotterFilter.subtype === 'custom'
            ? [blotterFilter.range.fromDate, blotterFilter.range.toDate]
            : computeRangeForCustomPresetISO(blotterFilter.preset),
      [blotterFilter],
    );

    blotterApi.useGetTodayTradesListQuery(
      isBlotterReady && blotterFilter.type === 'intraday' ? { showMyMainDeals } : skipToken,
    );
    blotterApi.useGetHistoricalTradesListQuery(
      isBlotterReady && blotterFilter.type === 'historical' && isDefined(fromDate) && isDefined(toDate)
        ? { showMyMainDeals, counterPartyBdrInitials, fromDate: fromDate, toDate: toDate }
        : skipToken,
    );
    blotterApi.useGetActiveOrdersListQuery(isBlotterReady ? { showMyMainDeals } : skipToken);

    return panelIsOpen ? (
      <BlotterPanel onRequestToHide={togglePanelVisibility} />
    ) : (
      <BlotterTrigger onTrigger={togglePanelVisibility} />
    );
  },
);
